import { defineStore } from 'pinia'

import { getPublicSettings } from '@/api/settings'
import { setGlobalCurrency } from '@/utils/formatters'

const toBool = (v) => v === true || v === 1 || v === '1' || v === 'true'

export const useSettingsStore = defineStore('settings', {
  state: () => ({
    currency: 'NGN',
    hotelName: '',
    phone: '',
    email: '',
    address: '',
    vatEnabled: false,
    vatPercentage: 0,
    horcEnabled: false,
    horcPercentage: 0,
    paystackEnabled: true,
    bankTransferEnabled: false,
    bankName: '',
    bankAccountName: '',
    bankAccountNumber: '',
    bankInstructions: '',
    social: {
      facebook: '',
      instagram: '',
      twitter: '',
      linkedin: '',
      youtube: '',
      tiktok: '',
      whatsapp: ''
    },
    loaded: false
  }),

  getters: {
    vatRate: (s) => (s.vatEnabled ? Number(s.vatPercentage) || 0 : 0),
    horcRate: (s) => (s.horcEnabled ? Number(s.horcPercentage) || 0 : 0),
    taxRate() {
      return this.vatRate + this.horcRate
    },
    // Splits a combined tax amount (as stored on charges/payments) into its VAT
    // and HORC components, apportioned by their configured rates. Returns a line
    // per active tax: { key, label, rate, amount }. The last line absorbs any
    // rounding remainder so the parts always sum back to the input.
    taxBreakdown() {
      const vatRate = this.vatRate
      const horcRate = this.horcRate
      const total = vatRate + horcRate
      return (taxTotal) => {
        const amount = Number(taxTotal) || 0
        if (amount <= 0 || total <= 0) return []
        const lines = []
        if (vatRate > 0) lines.push({ key: 'vat', label: 'VAT', rate: vatRate })
        if (horcRate > 0) lines.push({ key: 'horc', label: 'HORC', rate: horcRate })
        let allocated = 0
        return lines.map((line, i) => {
          const value =
            i === lines.length - 1
              ? Math.round((amount - allocated) * 100) / 100
              : Math.round((amount * line.rate) / total * 100) / 100
          allocated += value
          return { ...line, amount: value }
        })
      }
    }
  },

  actions: {
    apply(data) {
      if (data.currency) this.currency = data.currency
      this.hotelName = data.hotel_name || this.hotelName
      this.phone = data.phone || this.phone
      this.email = data.email || this.email
      this.address = data.address || this.address
      if ('vat_enabled' in data) this.vatEnabled = toBool(data.vat_enabled)
      if ('vat_percentage' in data) this.vatPercentage = Number(data.vat_percentage) || 0
      if ('horc_enabled' in data) this.horcEnabled = toBool(data.horc_enabled)
      if ('horc_percentage' in data) this.horcPercentage = Number(data.horc_percentage) || 0
      if ('paystack_enabled' in data) this.paystackEnabled = toBool(data.paystack_enabled)
      if ('bank_transfer_enabled' in data)
        this.bankTransferEnabled = toBool(data.bank_transfer_enabled)
      this.bankName = data.bank_name ?? this.bankName
      this.bankAccountName = data.bank_account_name ?? this.bankAccountName
      this.bankAccountNumber = data.bank_account_number ?? this.bankAccountNumber
      this.bankInstructions = data.bank_instructions ?? this.bankInstructions
      this.social = {
        facebook: data.facebook_url || '',
        instagram: data.instagram_url || '',
        twitter: data.twitter_url || '',
        linkedin: data.linkedin_url || '',
        youtube: data.youtube_url || '',
        tiktok: data.tiktok_url || '',
        whatsapp: data.whatsapp_number || ''
      }
      setGlobalCurrency(this.currency)
    },

    async load() {
      try {
        const res = await getPublicSettings()
        this.apply(res.data.data || res.data || {})
      } catch (e) {
        // Keep defaults if settings can't be reached.
      } finally {
        this.loaded = true
      }
    }
  }
})
